/**
 * Authentication Service
 *
 * All authentication-related API calls.
 * Maps directly to FastAPI endpoints in routes/auth.py
 */

import api from './api'

export const authService = {
  /**
   * Sign Up - Create new user account
   * POST /api/auth/signup
   *
   * @param {Object} data - Sign up form data
   * @param {string} data.email - User email
   * @param {string} data.password - User password
   * @param {string} [data.invitation_code] - Optional invitation code
   * @param {string} [data.address] - Fallback: property address (after 3 failed invitations)
   * @param {string} [data.zip_code] - Fallback: property zip code
   * @returns {Promise} - Response with user data
   */
  async signUp(data) {
    return api.post('/api/auth/signup', data)
  },

  /**
   * Sign In - Authenticate existing user
   * POST /api/auth/signin
   *
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} - Response with user data and session cookie
   */
  async signIn(email, password) {
    return api.post('/api/auth/signin', { email, password })
  },

  /**
   * Sign Out - End user session
   * POST /api/auth/signout
   *
   * @returns {Promise} - Success response
   */
  async signOut() {
    return api.post('/api/auth/signout')
  },

  /**
   * Request Password Reset - Send reset email
   * POST /api/auth/request-password-reset
   *
   * @param {string} email - User email
   * @returns {Promise} - Success message
   */
  async requestPasswordReset(email) {
    return api.post('/api/auth/request-password-reset', { email })
  },

  /**
   * Reset Password - Set new password using token
   * POST /api/auth/reset-password
   *
   * @param {string} token - Reset token from email link
   * @param {string} new_password - New password
   * @returns {Promise} - Response with user data (auto sign-in)
   */
  async resetPassword(token, new_password) {
    return api.post('/api/auth/reset-password', { token, new_password })
  },

  /**
   * Validate Invitation Code
   * POST /api/auth/validate-invitation
   *
   * @param {string} code - Invitation code
   * @returns {Promise} - Validation result
   */
  async validateInvitation(code) {
    return api.post('/api/auth/validate-invitation', { code })
  },

  /**
   * Get Session Status - Check if user is logged in
   * GET /api/session-status
   *
   * @returns {Promise} - Session data (is_signed_in, email, has_purchased_pack)
   */
  async getSessionStatus() {
    return api.get('/api/session-status')
  },

  /**
   * Refresh Session - Extend session timeout
   * POST /api/refresh-session
   *
   * Called when user clicks "Keep Working" in timeout warning
   *
   * @returns {Promise} - Success response
   */
  async refreshSession() {
    return api.post('/api/refresh-session')
  }
}

export default authService
